/**
 * PlayerReportRowContainer — binds one fixture row's actions (SE-6986).
 *
 * Same shape as every other row container: an inline arrow in a JSX prop is a
 * new function per render and the linter rejects it, so the ids bind here.
 * @module features/player-match-reports/components/molecules/PlayerReportRow/PlayerReportRowContainer
 */
import React, { memo, useCallback, useMemo } from "react";

import { formatCivilDate } from "@/features/fixture-planning/utils/toDateLabel";
import { TEST_ID_PLAYER_MATCH_REPORTS } from "@/e2e/testId";

import PlayerReportRowView from "./PlayerReportRowView";

import type { PlayerReportRow } from "../../../utils/toPlayerReportRows";

/** Props for PlayerReportRowContainer. */
export interface PlayerReportRowContainerProps {
  /** The fixture row. */
  readonly row: PlayerReportRow;
  /** Whose page this is, for the expanded panel's scope callout (SE-8016). */
  readonly playerName: string | null;
  /** Whether it is expanded, owned by the section. */
  readonly isExpanded: boolean;
  /** Expands or collapses, by match id. */
  readonly onToggleRow: (matchId: string) => void;
  /** Opens a full match report. */
  readonly onOpenReport: (reportId: string) => void;
  /** Test identity. */
  readonly testID?: string;
}

/**
 * Renders one fixture row with its actions bound.
 * @param root0 - Component props
 * @param root0.row - The fixture row
 * @param root0.playerName - Whose page this is
 * @param root0.isExpanded - Whether it is expanded
 * @param root0.onToggleRow - Expands or collapses
 * @param root0.onOpenReport - Opens a full match report
 * @param root0.testID - Test identity
 * @returns The row element.
 */
const PlayerReportRowContainer = ({
  row,
  playerName,
  isExpanded,
  onToggleRow,
  onOpenReport,
  testID,
}: PlayerReportRowContainerProps) => {
  const handleToggle = useCallback(
    () => onToggleRow(row.matchId),
    [onToggleRow, row.matchId]
  );

  // Bound to the OWNER's report: the row's expansion shows the owner first and
  // the link is the way into the full report, so it goes where the lead read
  // lives. The others' reports are reachable through the Reports list.
  const handleOpenOwnerReport = useCallback(() => {
    const owner = row.evaluations[0];
    if (owner !== undefined) onOpenReport(owner.reportId);
  }, [onOpenReport, row.evaluations]);

  const owner = row.evaluations[0] ?? null;
  const others = useMemo(() => row.evaluations.slice(1), [row.evaluations]);

  return (
    <PlayerReportRowView
      title={row.title}
      dateLabel={formatCivilDate(row.date)}
      competition={row.competition}
      playerName={playerName}
      owner={owner}
      others={others}
      isStale={row.hasStaleEvaluation}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      onOpenOwnerReport={handleOpenOwnerReport}
      testID={testID}
      scoresTestID={TEST_ID_PLAYER_MATCH_REPORTS.scoresFor(row.matchId)}
      scoutTestID={TEST_ID_PLAYER_MATCH_REPORTS.scoutFor(row.matchId)}
      scopeCalloutTestID={TEST_ID_PLAYER_MATCH_REPORTS.scopeCalloutFor(
        row.matchId
      )}
    />
  );
};

PlayerReportRowContainer.displayName = "PlayerReportRowContainer";

export default memo(PlayerReportRowContainer);
